/**
 * Open a fetched PDF blob in a new tab for inline viewing.
 * The PDF endpoint is auth-protected, so we can't link to it directly —
 * we fetch via axios, wrap in a blob URL, and navigate the new tab to it.
 *
 * The tab is opened synchronously (about:blank) before the async fetch so
 * popup blockers don't kill it. If the popup is blocked, we fall back to
 * navigating the current tab.
 */
export async function openPdfInNewTab(
  fetchBlob: () => Promise<Blob>,
  /** Called instead of navigating the current tab when the popup is blocked. */
  onBlocked?: (blob: Blob) => void,
): Promise<void> {
  const tab = window.open('', '_blank');
  try {
    const blob = await fetchBlob();
    const pdfBlob = blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' });
    const url = URL.createObjectURL(pdfBlob);
    if (tab && !tab.closed) {
      tab.location.href = url;
    } else if (onBlocked) {
      onBlocked(pdfBlob);
    } else {
      window.location.href = url;
    }
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (err) {
    tab?.close();
    throw err;
  }
}
